import { useEffect, useId } from 'react';

/**
 * Module-level registry of in-flight unsaved edits.
 *
 * The shared DetailPage discard guard (packages/ui) only intercepts the
 * navigation IT mediates — Cancel, back, a breadcrumb, and browser
 * `beforeunload`. It cannot see an EXTERNAL state change, such as switching the
 * global "Operate as tenant" scope ({@link CompanyScopeProvider}), that unmounts
 * a scoped edit form out from under it and would otherwise silently drop the
 * unsaved edit.
 *
 * This registry lets any active dirty editor announce itself (via
 * {@link useUnsavedEditGuard}) so the scope switch can confirm-before-discard.
 * It is a module singleton for the same reason the tenant scope
 * (`scope/companyScope`) is: the consumer (`setCompanyId`) needs a synchronous,
 * provider-order-independent read.
 *
 * A Set of opaque ids (one per mounted dirty editor) rather than a counter, so a
 * StrictMode double-invoke, or a remount that registers before the previous
 * unmount deregisters, cannot corrupt the flag.
 */
const dirtyEditors = new Set<string>();

/** Register `id` as having unsaved changes. */
export function markUnsavedEdit(id: string): void {
  dirtyEditors.add(id);
}

/** Deregister `id`. Clearing an id that was never registered is a no-op. */
export function clearUnsavedEdit(id: string): void {
  dirtyEditors.delete(id);
}

/** True while any registered editor still has unsaved changes. */
export function hasUnsavedEdits(): boolean {
  return dirtyEditors.size > 0;
}

/**
 * Register this component as having unsaved changes while `active` is true, so
 * the global scope switch can confirm before discarding them. Deregisters on
 * cleanup, so an unmount — or `active` flipping back to false — can never leave
 * a stuck flag.
 */
export function useUnsavedEditGuard(active: boolean): void {
  const id = useId();
  useEffect(() => {
    if (!active) return;
    markUnsavedEdit(id);
    return () => clearUnsavedEdit(id);
  }, [active, id]);
}
